/**
 * <shopla-opening-hours> — fills in a live opening-hours "popup link": the trigger shows the
 * current status ("Open. Closes at 18:00" / "Closed. Opens Monday at 09:00") and a dialog
 * (popup or drawer, handled by the surrounding <dialog-component>) lists this week's hours.
 *
 * The opening hours are read from the `shopla.opening-hours` collection metafield and embedded as
 * JSON in `data-opening-hours`. Shape (see shopla-shopify):
 *
 *   {
 *     "timezone": "Europe/Berlin",
 *     "weekly": { "monday": [{ "opens": "09:00", "closes": "18:00" }], "sunday": [] },
 *     "exceptions": [
 *       { "from": "2026-12-25", "closed": true, "name": "Christmas" },
 *       { "from": "2026-07-14", "to": "2026-07-28", "closed": true, "name": "Summer vacation" },
 *       { "from": "2026-12-24", "closed": false, "hours": [{ "opens": "09:00", "closes": "13:00" }], "name": "Christmas Eve" }
 *     ]
 *   }
 *
 * A missing weekday (or an empty list) means closed that day. An exception covers a single date
 * (`from`) or an inclusive range (`from`..`to`) and overrides the weekly hours.
 *
 * Status is computed on the client on purpose: Shopify caches the rendered page, so a server-side
 * "now" would go stale. Opening times are wall-clock times in the shop's own `timezone` (an IANA
 * name), so we take the always-accurate current instant (`Date.now()`) and read it *in that
 * timezone* via `Intl.DateTimeFormat` — correct no matter where the visitor is.
 *
 * When the payload carries no `timezone`, the location's ISO country code (`data-country-code`,
 * from the `shopla.country-code` metafield) stands in — exact for the countries that keep a single
 * timezone, DST rules included. With neither, the shop's wall clock is genuinely unknown, and the
 * block says so rather than guessing: a neutral "Opening hours" label over the plain weekly
 * schedule, no Open/Closed claim.
 *
 * What it must NOT fall back to is the Shopify store's UTC offset (Liquid's `%z`), which is what
 * this block used to do. That offset is the merchant admin's own timezone: it says nothing about
 * where a shop stands, so a store set to America/New_York reported a Zürich shop "Open. Closes at
 * 18:00" at 19:25 local time. It is also captured when the page is rendered, so Shopify's page
 * cache would carry it across a DST change even where it happened to match the shop.
 */

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
// Maps the English short weekday name Intl produces (en-US) to our weekday keys.
const WEEKDAY_BY_ABBR = { Sun: 'sunday', Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday', Thu: 'thursday', Fri: 'friday', Sat: 'saturday' };
const DAY_MS = 24 * 60 * 60 * 1000;
const UPDATE_INTERVAL_MS = 60 * 1000;

/** Minutes since midnight for a "HH:MM" string, or null when it can't be parsed. */
function parseMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const minutes = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  return Number.isFinite(minutes) ? minutes : null;
}

/**
 * IANA timezone per ISO 3166-1 alpha-2 country code, for locations whose opening hours carry no
 * `timezone` of their own.
 *
 * Only countries that observe ONE wall clock across the whole territory belong here, so the code
 * pins the timezone exactly rather than approximating it. Countries spanning several zones (US, CA,
 * AU, RU, BR, MX, …) and those whose islands keep a different clock from the mainland (ES with the
 * Canaries, PT with the Azores) are deliberately absent: for those the country says too little, and
 * a missing entry degrades to "unknown", which is honest, rather than to a wrong Open/Closed.
 * Europe is covered because that is where Shopla's shops are; adding a country is one line.
 */
const TIMEZONE_BY_COUNTRY = {
  AD: 'Europe/Andorra', AT: 'Europe/Vienna', BE: 'Europe/Brussels', BG: 'Europe/Sofia',
  CH: 'Europe/Zurich', CZ: 'Europe/Prague', DE: 'Europe/Berlin', DK: 'Europe/Copenhagen',
  EE: 'Europe/Tallinn', FI: 'Europe/Helsinki', FR: 'Europe/Paris', GB: 'Europe/London',
  GR: 'Europe/Athens', HR: 'Europe/Zagreb', HU: 'Europe/Budapest', IE: 'Europe/Dublin',
  IS: 'Atlantic/Reykjavik', IT: 'Europe/Rome', LI: 'Europe/Vaduz', LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg', LV: 'Europe/Riga', MC: 'Europe/Monaco', MT: 'Europe/Malta',
  NL: 'Europe/Amsterdam', NO: 'Europe/Oslo', PL: 'Europe/Warsaw', RO: 'Europe/Bucharest',
  SE: 'Europe/Stockholm', SI: 'Europe/Ljubljana', SK: 'Europe/Bratislava', SM: 'Europe/San_Marino',
  VA: 'Europe/Vatican',
};

/** Whether `Intl` knows this timezone name — a typo'd or dropped zone must not read as valid. */
function isKnownTimeZone(name) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * The IANA timezone a location's wall clock runs on: its own `timezone` when the metafield carries
 * a usable one, else the one its country implies, else null — "unknown", never a guess.
 *
 * @param {Record<string, any>} data - Parsed `shopla.opening-hours` value.
 * @param {string | undefined} countryCode - The location's ISO country code.
 * @returns {string | null}
 */
export function resolveTimezone(data, countryCode) {
  const own = String(data?.timezone ?? '').trim();
  if (own && isKnownTimeZone(own)) return own;
  const byCountry = TIMEZONE_BY_COUNTRY[String(countryCode ?? '').trim().toUpperCase()];
  return byCountry && isKnownTimeZone(byCountry) ? byCountry : null;
}

/** `renderedWeekKey` sentinel for the dateless plain week (real weeks key on their first date). */
const PLAIN_WEEK_KEY = 'plain';

/** A Monday-to-Sunday reference week — 2024-01-01 was a Monday — so a weekday can still be named
 *  in the visitor's language when no real date is known. */
const REFERENCE_WEEK_ISO = {
  monday: '2024-01-01', tuesday: '2024-01-02', wednesday: '2024-01-03', thursday: '2024-01-04',
  friday: '2024-01-05', saturday: '2024-01-06', sunday: '2024-01-07',
};

export class ShoplaOpeningHours extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.data = this.readData();
    this.timezone = resolveTimezone(this.data, this.dataset.countryCode);
    this.locale = this.dataset.locale || document.documentElement.lang || undefined;
    this.use12Hour = this.dataset.timeFormat === '12';
    this.showDates = this.dataset.showDates !== 'false';
    this.labels = {
      open: this.dataset.labelOpen || 'Open',
      closed: this.dataset.labelClosed || 'Closed',
      opensAt: this.dataset.labelOpensAt || 'Opens at {time}',
      opensOn: this.dataset.labelOpensOn || 'Opens {day} at {time}',
      closesAt: this.dataset.labelClosesAt || 'Closes at {time}',
      closesOn: this.dataset.labelClosesOn || 'Closes {day} at {time}',
      legend: this.dataset.labelLegend || 'special opening hours',
      // Stands in for the status word when the shop's timezone is unknown.
      title: this.dataset.labelTitle || 'Opening hours',
    };

    // The trigger state/detail, the dialog list and the legend are rendered by Liquid; we fill them.
    this.stateEl = this.querySelector('.shopla-opening-hours__state');
    this.detailEl = this.querySelector('.shopla-opening-hours__detail');
    this.list = this.querySelector('.shopla-opening-hours__list');
    this.legendEl = this.querySelector('.shopla-opening-hours__legend');

    // With no hours at all, replace the block with a plain "no opening hours" message.
    if (!this.stateEl || !this.list || !this.hasAnyHours()) {
      this.showEmpty();
      return;
    }

    this.update();
    // Without a timezone the block shows a fixed weekly schedule — nothing to re-check each minute.
    if (this.timezone) this.timer = window.setInterval(() => this.update(), UPDATE_INTERVAL_MS);
  }

  disconnectedCallback() {
    if (this.timer) window.clearInterval(this.timer);
  }

  /** Replaces the block with a plain text message when there are no opening hours. */
  showEmpty() {
    const root = this.closest('dialog-component') || this;
    const label = this.dataset.labelNoData;
    if (label) {
      const message = document.createElement('rte-formatter');
      message.className = 'spacing-style text-block shopla-opening-hours__empty';
      message.textContent = label;
      root.replaceWith(message);
    } else {
      root.hidden = true;
    }
  }

  readData() {
    try {
      const parsed = JSON.parse(this.dataset.openingHours || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  hasAnyHours() {
    const weekly = this.data.weekly || {};
    const hasWeekly = WEEKDAYS.some((day) => Array.isArray(weekly[day]) && weekly[day].length > 0);
    const hasExceptions = Array.isArray(this.data.exceptions) && this.data.exceptions.length > 0;
    return hasWeekly || hasExceptions;
  }

  /* ----------------------------------------------------------------- time */

  /**
   * The shop's "now" as { isoDate, minutes (since midnight), weekdayKey }, or null when the shop's
   * timezone is unknown. Read from the absolute instant (`Date.now()`) in the shop's own timezone,
   * so it is correct wherever the visitor is — and the visitor's own clock is deliberately not a
   * fallback: it says where the VISITOR is, which is no evidence about the shop.
   */
  currentMoment() {
    if (!this.timezone) return null;
    return ShoplaOpeningHours.zonedParts(new Date(Date.now()), this.timezone);
  }

  /** Reads an absolute instant in a given IANA timezone, or null if the timezone is invalid. */
  static zonedParts(date, timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', weekday: 'short', hourCycle: 'h23',
      }).formatToParts(date);
      const map = {};
      for (const part of parts) map[part.type] = part.value;
      const hour = parseInt(map.hour, 10) % 24; // h23 can emit "24" at midnight in some engines
      const minutes = hour * 60 + parseInt(map.minute, 10);
      const weekdayKey = WEEKDAY_BY_ABBR[map.weekday];
      if (!weekdayKey || !Number.isFinite(minutes)) return null;
      return { isoDate: `${map.year}-${map.month}-${map.day}`, minutes, weekdayKey };
    } catch (error) {
      return null;
    }
  }

  /** "YYYY-MM-DD" for a wall-clock Date (read via UTC fields). */
  static isoDate(date) {
    return date.toISOString().slice(0, 10);
  }

  /** A noon-UTC Date for an ISO date string, safe for whole-day stepping. */
  static dateFromIso(isoDate) {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12));
  }

  /** Weekday key ('monday'…'sunday') for a wall-clock Date. */
  static weekdayKey(date) {
    return WEEKDAYS[(date.getUTCDay() + 6) % 7];
  }

  /* ------------------------------------------------------------ schedule */

  /** The exception covering an ISO date, or null. ISO strings compare chronologically. */
  exceptionFor(isoDate) {
    if (!isoDate) return null;
    const exceptions = Array.isArray(this.data.exceptions) ? this.data.exceptions : [];
    return (
      exceptions.find((exception) => {
        if (!exception || !exception.from) return false;
        const to = exception.to || exception.from;
        return isoDate >= exception.from && isoDate <= to;
      }) || null
    );
  }

  /** Effective hours for a given date: the exception's hours if any, otherwise the weekly hours.
   *  `isoDate` may be null (unknown date), in which case only the weekly hours apply. */
  scheduleFor(isoDate, weekdayKey) {
    const exception = this.exceptionFor(isoDate);
    if (exception) {
      const ranges = !exception.closed && Array.isArray(exception.hours) ? exception.hours : [];
      return {
        closed: exception.closed || ranges.length === 0,
        ranges,
        exceptionName: exception.name || '',
        isException: true,
      };
    }
    const weekly = this.data.weekly || {};
    const ranges = Array.isArray(weekly[weekdayKey]) ? weekly[weekdayKey] : [];
    return { closed: ranges.length === 0, ranges, exceptionName: '', isException: false };
  }

  /** Whether the shop is open at `minutes` (minutes since midnight) given a day's schedule. */
  static isOpenAt(schedule, minutes) {
    if (schedule.closed) return false;
    return schedule.ranges.some((range) => {
      const opens = parseMinutes(range.opens);
      const closes = parseMinutes(range.closes);
      return opens != null && closes != null && opens <= minutes && minutes < closes;
    });
  }

  /** The seven days starting today (today + the next 6), so past days of the week aren't shown. */
  weekDays(todayIso) {
    const start = ShoplaOpeningHours.dateFromIso(todayIso);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start.getTime() + i * DAY_MS);
      const isoDate = ShoplaOpeningHours.isoDate(date);
      const weekdayKey = ShoplaOpeningHours.weekdayKey(date);
      days.push({
        isoDate,
        weekdayKey,
        isToday: isoDate === todayIso,
        ...this.scheduleFor(isoDate, weekdayKey),
      });
    }
    return days;
  }

  /**
   * The weekly schedule as plain Monday→Sunday, for when the shop's timezone — and so which day is
   * "today" — is unknown. Date-specific exceptions can't be placed without a date, so only the
   * weekly hours show, which is exactly as much as the block can honestly claim.
   */
  plainWeek() {
    return WEEKDAYS.map((weekdayKey) => ({
      isoDate: null,
      weekdayKey,
      isToday: false,
      ...this.scheduleFor(null, weekdayKey),
    }));
  }

  /** First opening within the next 7 days as { dayOffset, isoDate, opens }, or null. */
  nextOpening(todayIso, nowMinutes) {
    const reference = ShoplaOpeningHours.dateFromIso(todayIso);
    for (let offset = 0; offset <= 7; offset++) {
      const date = new Date(reference.getTime() + offset * DAY_MS);
      const isoDate = ShoplaOpeningHours.isoDate(date);
      const schedule = this.scheduleFor(isoDate, ShoplaOpeningHours.weekdayKey(date));
      if (schedule.closed) continue;
      const opensTimes = schedule.ranges
        .map((range) => parseMinutes(range.opens))
        .filter((minutes) => minutes != null && (offset > 0 || minutes > nowMinutes))
        .sort((a, b) => a - b);
      if (opensTimes.length) return { dayOffset: offset, isoDate, opens: opensTimes[0] };
    }
    return null;
  }

  /* ------------------------------------------------------------ formatting */

  /** Localized weekday name / short date for an ISO date (rendered at noon UTC to avoid drift). */
  formatPart(isoDate, options) {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12));
    try {
      return new Intl.DateTimeFormat(this.locale, { ...options, timeZone: 'UTC' }).format(date);
    } catch (error) {
      return new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' }).format(date);
    }
  }

  weekdayName(isoDate) {
    return this.formatPart(isoDate, { weekday: 'long' });
  }

  shortDate(isoDate) {
    return this.formatPart(isoDate, { day: 'numeric', month: 'short' });
  }

  /** "09:00" in 24h, or "9:00 AM" in 12h. */
  formatTime(value) {
    const minutes = parseMinutes(value);
    if (minutes == null) return String(value ?? '');
    const hours = Math.floor(minutes / 60);
    const mins = String(minutes % 60).padStart(2, '0');
    if (!this.use12Hour) return `${String(hours).padStart(2, '0')}:${mins}`;
    const period = hours < 12 ? 'AM' : 'PM';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${mins} ${period}`;
  }

  formatRanges(ranges) {
    return ranges
      .map((range) => `${this.formatTime(range.opens)} – ${this.formatTime(range.closes)}`)
      .join(', ');
  }

  minutesToTime(minutes) {
    const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mins = String(minutes % 60).padStart(2, '0');
    return this.formatTime(`${hours}:${mins}`);
  }

  static template(value, replacements) {
    return Object.keys(replacements).reduce(
      (result, key) => result.replace(`{${key}}`, replacements[key]),
      value
    );
  }

  /* ------------------------------------------------------------ rendering */

  /**
   * The part after the state word: the next change, prefixed with ". " — e.g. ". Closes at 18:00"
   * or ". Opens Monday at 09:00". Empty when nothing is scheduled (so the trigger is just the word).
   */
  detailText(isoDate, today, nowMinutes, open) {
    let detail = '';
    if (open) {
      // The closing time of the range we're currently within.
      const closesMinutes = today.ranges
        .map((range) => ({ opens: parseMinutes(range.opens), closes: parseMinutes(range.closes) }))
        .filter((range) => range.opens != null && range.closes != null && range.opens <= nowMinutes && nowMinutes < range.closes)
        .map((range) => range.closes)
        .sort((a, b) => a - b)[0];
      if (closesMinutes != null) detail = ShoplaOpeningHours.template(this.labels.closesAt, { time: this.minutesToTime(closesMinutes) });
    } else {
      const next = this.nextOpening(isoDate, nowMinutes);
      if (next) {
        detail =
          next.dayOffset === 0
            ? ShoplaOpeningHours.template(this.labels.opensAt, { time: this.minutesToTime(next.opens) })
            : ShoplaOpeningHours.template(this.labels.opensOn, { day: this.weekdayName(next.isoDate), time: this.minutesToTime(next.opens) });
      }
    }
    return detail ? `. ${detail}` : '';
  }

  renderTable(days) {
    this.list.textContent = '';
    days.forEach((day) => {
      // A flex row (day pushed left, hours pushed right) so both sides sit flush against the
      // dialog's padding — equal on every side, with no table intrinsic-sizing quirks.
      const row = document.createElement('div');
      row.className = 'shopla-opening-hours__row';
      if (day.isToday) row.classList.add('is-today');
      if (day.closed) row.classList.add('is-closed');
      if (day.isException) row.classList.add('is-exception');

      const head = document.createElement('span');
      head.className = 'shopla-opening-hours__day';
      const name = document.createElement('span');
      name.className = 'shopla-opening-hours__day-name';
      // A dateless row (the plain week) still names its weekday, via the reference week.
      name.textContent = this.weekdayName(day.isoDate || REFERENCE_WEEK_ISO[day.weekdayKey]);
      head.append(name);
      if (this.showDates && day.isoDate) {
        const date = document.createElement('span');
        date.className = 'shopla-opening-hours__day-date';
        date.textContent = this.shortDate(day.isoDate);
        head.append(date);
      }

      const cell = document.createElement('span');
      cell.className = 'shopla-opening-hours__hours';
      const times = document.createElement('span');
      times.className = 'shopla-opening-hours__times';
      times.textContent = day.closed ? this.labels.closed : this.formatRanges(day.ranges);
      cell.append(times);
      // Special (exception) days are flagged with an asterisk explained by the legend below.
      if (day.isException) {
        const marker = document.createElement('span');
        marker.className = 'shopla-opening-hours__marker';
        marker.setAttribute('aria-hidden', 'true');
        marker.textContent = '*';
        cell.append(marker);
      }

      row.append(head, cell);
      this.list.append(row);
    });

    // Show the "* special opening hours" legend only when this week has a special day.
    const hasException = days.some((day) => day.isException);
    if (this.legendEl) {
      this.legendEl.hidden = !hasException;
      this.legendEl.textContent = hasException ? `* ${this.labels.legend}` : '';
    }
  }

  /** Recomputes the trigger status line and the dialog table for the current moment. */
  update() {
    const moment = this.currentMoment();

    // The shop's wall clock is unknown, so whether it is open right now is unknowable: show the
    // neutral title and the plain weekly schedule rather than a status that would only look certain.
    if (!moment) {
      this.stateEl.textContent = this.labels.title;
      this.detailEl.textContent = '';
      this.classList.remove('is-open-now', 'is-closed-now');
      if (this.renderedWeekKey !== PLAIN_WEEK_KEY) {
        this.renderTable(this.plainWeek());
        this.renderedWeekKey = PLAIN_WEEK_KEY;
        this.renderedTodayIso = null;
      }
      return;
    }

    const { isoDate, minutes, weekdayKey } = moment;
    const today = this.scheduleFor(isoDate, weekdayKey);
    const open = ShoplaOpeningHours.isOpenAt(today, minutes);

    // Only the state word ("Open"/"Closed") is colored; the rest stays in the normal text color.
    this.stateEl.textContent = open ? this.labels.open : this.labels.closed;
    this.detailEl.textContent = this.detailText(isoDate, today, minutes, open);
    this.classList.toggle('is-open-now', open);
    this.classList.toggle('is-closed-now', !open);

    // Rebuild the table only when the visible week changes (cheap, and keeps "today" correct).
    const days = this.weekDays(isoDate);
    const weekKey = days[0].isoDate;
    if (weekKey !== this.renderedWeekKey || this.renderedTodayIso !== isoDate) {
      this.renderTable(days);
      this.renderedWeekKey = weekKey;
      this.renderedTodayIso = isoDate;
    }
  }
}

if (!customElements.get('shopla-opening-hours')) {
  customElements.define('shopla-opening-hours', ShoplaOpeningHours);
}
